import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import DashboardHeaderRow from "../DashboardHeaderRow";
import QuickFilterButton from "../../../common/components/functional/quickFilterButton/QuickFilterButton";
import {
  OrderQuickFilterButton,
  OrderQuickFilterButtonType,
  getOrderQuickFilterPreset,
  ORDER_QUICK_FILTER_LOCAL_STORAGE,
} from "../../orders/filters/common";
import {
  setFiltersToLocalStorage,
  setQuickFilterToLocalStorage,
} from "../../../common/components/functional/FilterSearchField/helpers";
import { ORDER_FILTERS_LOCAL_STORAGE } from "../../orders/constants";
import { useAppSelector } from "../../../app/hooks";

const ORDER_LIST_LINK = "/home/orders";

const Divider = ({ height }: { height: string }) => (
  <Box
    height={height}
    width="1px"
    border="1px solid #000A441C"
    margin="0px 16px"
  />
);

const DashboardOrders = () => {
  const { t } = useTranslation("dashboard");
  const { user } = useAppSelector((state) => state.user);

  const handleClick = (quickFilterButtonType: OrderQuickFilterButtonType) => {
    const preset = getOrderQuickFilterPreset(
      quickFilterButtonType,
      user?.id,
      t,
    );
    setFiltersToLocalStorage(preset, ORDER_FILTERS_LOCAL_STORAGE);
    setQuickFilterToLocalStorage(
      quickFilterButtonType,
      ORDER_QUICK_FILTER_LOCAL_STORAGE,
    );
  };

  const quickFilterConfigs = {
    [OrderQuickFilterButton.HANDLER_OWN_OPEN]: {
      text: t("order:handlerOwnOpen"),
      action: () => handleClick(OrderQuickFilterButton.HANDLER_OWN_OPEN),
      link: ORDER_LIST_LINK,
    },
    [OrderQuickFilterButton.SELLER_OWN_OPEN]: {
      text: t("order:sellerOwnOpen"),
      action: () => handleClick(OrderQuickFilterButton.SELLER_OWN_OPEN),
      link: ORDER_LIST_LINK,
    },
    [OrderQuickFilterButton.TEAM_ACTION_NEEDED]: {
      text: t("order:teamNeedAction"),
      action: () => handleClick(OrderQuickFilterButton.TEAM_ACTION_NEEDED),
      link: ORDER_LIST_LINK,
    },
    [OrderQuickFilterButton.TEAM_OPEN_SHORT]: {
      text: t("order:teamOpenShort"),
      action: () => handleClick(OrderQuickFilterButton.TEAM_OPEN_SHORT),
      link: ORDER_LIST_LINK,
    },
    [OrderQuickFilterButton.TEAM_OPEN_LONG]: {
      text: t("order:teamOpenLong"),
      action: () => handleClick(OrderQuickFilterButton.TEAM_OPEN_LONG),
      link: ORDER_LIST_LINK,
    },
  };

  // TODO: placeholders will be replaced with actual data after 1.2 release
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <DashboardHeaderRow title={t("orders.title")} />
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          bgcolor: "#fff",
          borderRadius: "8px",
          padding: "16px 32px",
        }}
      >
        <Box>
          <Box display="flex" minHeight="36px" alignItems="center">
            <Box paddingRight="12px">
              <QuickFilterButton
                {...quickFilterConfigs[OrderQuickFilterButton.HANDLER_OWN_OPEN]}
              />
            </Box>
            <Box paddingRight="12px">
              <QuickFilterButton
                {...quickFilterConfigs[OrderQuickFilterButton.SELLER_OWN_OPEN]}
              />
            </Box>
            <Box paddingRight="12px">
              <QuickFilterButton
                {...quickFilterConfigs[
                  OrderQuickFilterButton.TEAM_ACTION_NEEDED
                ]}
              />
            </Box>
            <Box paddingRight="12px">
              <QuickFilterButton
                {...quickFilterConfigs[OrderQuickFilterButton.TEAM_OPEN_SHORT]}
              />
            </Box>
            <Box>
              <QuickFilterButton
                {...quickFilterConfigs[OrderQuickFilterButton.TEAM_OPEN_LONG]}
              />
            </Box>
          </Box>
          <Box>
            <Box
              height="33px"
              display="flex"
              alignItems="flex-end"
              marginBottom="8px"
            >
              <Typography
                variant="header"
                sx={{
                  textTransform: "uppercase",
                  fontWeight: "700",
                  fontSize: "14px",
                  lineHeight: "16.8px",
                }}
              >
                {t("orders.teamSituationTodayPlaceholder")}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Box width="180px">
                <Typography color="textSecondary">
                  {t("orders.talentsEmployedAutomaticallyPlaceholder")}
                </Typography>
              </Box>
              <Divider height="100px" />
              <Box width="180px">
                <Typography color="textSecondary">
                  {t("orders.talentBeingMatchedAutomatically")}
                </Typography>
              </Box>
              <Divider height="100px" />
              <Box width="180px">
                <Typography color="textSecondary">
                  {t("orders.talentBeingMatchedForLongTermWork")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardOrders;
